/**
 * The first screen: what jobs are on this phone, and what does each still owe the office.
 *
 * Ordered newest first, because the job a rep wants is almost always the one they just
 * started. The per-job counts are here rather than only on the banner so that a crew
 * finishing the day can see *which* job is still holding evidence, not just that something
 * somewhere is queued.
 */

import type { JobSummary } from '../db/repository.js';
import { Chip } from './components.js';
import { styles } from './styles.js';

export function JobListScreen({
  jobs,
  loading,
  onOpen,
  onNew,
}: {
  jobs: readonly JobSummary[] | undefined;
  loading: boolean;
  onOpen: (jobId: string) => void;
  onNew: () => void;
}) {
  return (
    <>
      <button
        type="button"
        style={{ ...styles.button, ...styles.buttonPrimary }}
        onClick={onNew}
        data-testid="new-inspection"
      >
        Start an inspection
      </button>

      {loading && jobs === undefined && <p style={styles.cardMeta}>Opening local storage…</p>}

      {jobs !== undefined && jobs.length === 0 && (
        <p style={styles.cardMeta}>
          No inspections on this device yet. Starting one works with no signal.
        </p>
      )}

      {jobs?.map((summary) => (
        <button
          key={summary.job.jobId}
          type="button"
          style={styles.card}
          onClick={() => {
            onOpen(summary.job.jobId);
          }}
        >
          <p style={styles.cardTitle}>{summary.job.address}</p>
          <p style={styles.cardMeta}>
            {summary.job.homeownerName} · {summary.job.claimNumber || 'no claim number'} ·{' '}
            {String(summary.requiredSatisfied)}/{String(summary.requiredTotal)} required shots
          </p>
          <p style={{ ...styles.cardMeta, display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <Chip
              status={{
                tone: 'info',
                text: `${String(summary.photoCount)} photo${summary.photoCount === 1 ? '' : 's'}`,
              }}
            />
            {summary.queued > 0 && (
              <Chip status={{ tone: 'warn', text: `${String(summary.queued)} on device` }} />
            )}
            {summary.blocked > 0 && (
              <Chip status={{ tone: 'bad', text: `${String(summary.blocked)} need attention` }} />
            )}
          </p>
        </button>
      ))}
    </>
  );
}
